import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { NetworkSelected } from 'types';

type Render<T> = (item: T, props: {
  onChange?: (item: T, changes: Partial<T>) => void;
  onRemove: (item: T) => void;
}) => React.ReactNode;

type LabelRender<T> = (item: T) => React.ReactNode;

export type TableColumn<T> = {
  key: string;
  label: LabelRender<T> | string;
  render?: Render<T> | null;
  className?: string;
};

type SelectedListProps<T = NetworkSelected> = {
  data: T[];
  onChange?: (item: T, changes: Partial<T>) => void;
  onRemove: (item: T) => void;
  columns?: TableColumn<T>[];
};

/**
 * Dynamic SelectedList table.
 * - `columns` drives the table columns. Each column has `key`, `label`, and optional `render`.
 * - If `render` is null or undefined, the table will display `item[key]`.
 */
function SelectedList<T extends Record<string, any>>({
  data,
  onChange,
  onRemove,
  columns,
}: Readonly<SelectedListProps<T>>) {
  const cols: TableColumn<T>[] = columns && columns.length > 0
    ? columns
    : [
      { key: 'deviceName', label: 'Room/Zone/Group Name', render: null },
      { key: 'deviceType', label: 'Type', render: null },
      { key: 'gatewayName', label: 'Network Name', render: null },
      { key: 'gatewayId', label: 'Network ID', render: null },
    ];

  return (
    <Row className="flex-fill cstm-table">
      <Col>
        {/* Header row */}
        <Row className="fw-bold pb-1 mb-2 d-none d-sm-flex px-2" >
          {cols.map((c) => {
            const labelContent = typeof c.label === 'function'
              ? c.label(data[0] ?? {} as T)
              : c.label;
            return (
              <Col key={c.key} className={`${c.className ?? ""}`}>
                <span>{labelContent}</span>
              </Col>
            )
          })}
        </Row>

        {/* Body rows */}
        {data && data.length > 0 ? (
          data.map((item, idx) => (
            <Row key={item.deviceId + idx} className="rounded bg-secondary align-items-center px-2 py-2 mb-2 text-14px">
              {cols.map((c) => {
                const itemValue = item[c.key] ?? '';
                return (
                  <Col key={c.key} className={c.className}>
                    {c.render ? c.render(item, { onChange, onRemove }) : <span title={itemValue}>{itemValue}</span>}
                  </Col>
                )
              })
              }
            </Row>
          ))
        ) : (
          <Row>
            <Col className="text-center text-muted">No items</Col>
          </Row>
        )}
      </Col>
    </Row>
  );
}

export default SelectedList;

